import { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
    disableVehicle,
    enableVehicle,
    getVehicles,
    saveVehicle,
    VehiclesWithFilterNPaginationResponse,
} from "../../../services/vehicleService";
import { messageErrorConfirmDialog, messageSuccessConfirmDialog, PropsMessageDialog } from "../../../components/message-dialog";
import { VehicleViewModel } from "../../../models/vehicles.model";

const PAGE_SIZE = 10;

const vehicleViewInitial: VehicleViewModel = {
    id: undefined,
    name: "",
    description: "",
    mechanism_action: "",
    PH: "",
    incompatibility: "",
    minimum: "",
    maximum: "",
    source: "",
};

const vehicleSchema = Yup.object().shape({
    name: Yup.string().required("Este campo é obrigatório"),
    description: Yup.string().required("Este campo é obrigatório"),
    mechanism_action: Yup.string().required("Este campo é obrigatório"),
    PH: Yup.string().required("Este campo é obrigatório"),
    incompatibility: Yup.string().required("Este campo é obrigatório"),
    minimum: Yup.string().required("Este campo é obrigatório"),
    maximum: Yup.string().required("Este campo é obrigatório"),
    source: Yup.string().required("Este campo é obrigatório"),
});

/** Estado, formik, fetch e handlers da administração de veículos. */
export function useVehiclePage() {
    // ── data state ──────────────────────────────────────────────────────
    const [rowsVehicles, setRowsVehicles] = useState<VehiclesWithFilterNPaginationResponse[]>([]);
    const [loading, setLoading] = useState(false);

    // ── search / pagination ─────────────────────────────────────────────
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    // ── detail dialog (ProductDetail — untouched) ────────────────────────
    const [detailOpen, setDetailOpen] = useState(false);
    const [selectedVehicle, setSelectedVehicle] = useState<VehicleViewModel>({} as VehicleViewModel);

    // ── form modal ───────────────────────────────────────────────────────
    const [modalOpen, setModalOpen] = useState(false);
    const [vehicleViewModel, setVehicleViewModel] = useState<VehicleViewModel>(vehicleViewInitial);
    const [btnSaveDisabled, setBtnSaveDisabled] = useState(false);

    // ── message dialog ───────────────────────────────────────────────────
    const [dialogModel, setDialogModel] = useState<PropsMessageDialog>({
        onConfirm: () => {
            setDialogModel((prev) => ({ ...prev, open: false }));
        },
        open: false,
    } as PropsMessageDialog);

    // ── formik (initialValues / validationSchema / onSubmit byte-identical) ─
    const formik = useFormik({
        initialValues: vehicleViewInitial,
        validationSchema: vehicleSchema,
        onSubmit: () => {
            registerVehicle();
        },
    });

    // ── helpers ──────────────────────────────────────────────────────────

    const updateFormikValues = (fk: typeof formik, values: VehicleViewModel) => {
        Object.entries(values).forEach(([key, val]) => {
            (fk.values as any)[key] = val;
        });
    };

    const resetForm = () => {
        setVehicleViewModel({ ...vehicleViewInitial });
    };

    // ── services (untouched logic) ───────────────────────────────────────

    const loadVehicles = (callbackFinally?: () => void) => {
        getVehicles()
            .then((result) => {
                if (result.status === 200) {
                    const vehiclesList = result.data;
                    if (Array.isArray(vehiclesList)) {
                        setRowsVehicles(vehiclesList);
                    } else if (vehiclesList && Array.isArray(vehiclesList.data)) {
                        setRowsVehicles(vehiclesList.data);
                    } else if (vehiclesList && Array.isArray(vehiclesList.vehicles)) {
                        setRowsVehicles(vehiclesList.vehicles);
                    } else {
                        setRowsVehicles([]);
                    }
                }
            })
            .catch(() => {
                setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
                setRowsVehicles([]);
            })
            .finally(() => {
                if (callbackFinally != null) callbackFinally();
            });
    };

    const registerVehicle = () => {
        setBtnSaveDisabled(true);
        setLoading(true);

        saveVehicle(vehicleViewModel)
            .then(() => {
                const message = vehicleViewModel.id == null ? "Veículo cadastrado com sucesso!" : "Veículo alterado com sucesso";
                setDialogModel({ ...messageSuccessConfirmDialog(dialogModel.onConfirm, message) });
                resetForm();
                setModalOpen(false);
                loadVehicles();
            })
            .catch(() => setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) }))
            .finally(() => {
                setLoading(false);
                setBtnSaveDisabled(false);
            });
    };

    const handleClickEnableVehicle = (vehicle: VehiclesWithFilterNPaginationResponse) => {
        let message = "Veículo desabilitado com sucesso";
        let promise = disableVehicle;

        if (vehicle.enabled === 0) {
            message = "Veículo habilitado com sucesso";
            promise = enableVehicle;
        }

        promise(vehicle.id.toString())
            .then(() => {
                loadVehicles();
                setDialogModel({
                    ...messageSuccessConfirmDialog(dialogModel.onConfirm, message),
                });
            })
            .catch(() => {
                setDialogModel({ ...messageErrorConfirmDialog(dialogModel.onConfirm) });
            });
    };

    const handleClickViewVehicle = (vehicle: VehiclesWithFilterNPaginationResponse) => {
        const mappedVehicle: VehicleViewModel = {
            id: vehicle.id,
            name: vehicle.name,
            description: vehicle.description,
            mechanism_action: vehicle.mechanism_action,
            PH: vehicle.PH,
            incompatibility: vehicle.incompatibility,
            minimum: vehicle.minimum.toString(),
            maximum: vehicle.maximum.toString(),
            source: vehicle.source,
            favorite: vehicle.favorite === 1,
            enabled: vehicle.enabled === 1,
        };
        setSelectedVehicle(mappedVehicle);
        setDetailOpen(true);
    };

    const handleClickEditVehicle = (vehicle: VehiclesWithFilterNPaginationResponse) => {
        const mappedVehicle: VehicleViewModel = {
            id: vehicle.id,
            name: vehicle.name,
            description: vehicle.description,
            mechanism_action: vehicle.mechanism_action,
            PH: vehicle.PH,
            incompatibility: vehicle.incompatibility,
            minimum: vehicle.minimum.toString(),
            maximum: vehicle.maximum.toString(),
            source: vehicle.source,
            favorite: vehicle.favorite === 1,
            enabled: vehicle.enabled === 1,
        };
        setVehicleViewModel(mappedVehicle);
        setModalOpen(true);
    };

    const openCreate = () => {
        resetForm();
        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);
        resetForm();
    };

    // ── effects ──────────────────────────────────────────────────────────

    useEffect(() => {
        setLoading(true);
        loadVehicles(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        updateFormikValues(formik, vehicleViewModel);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vehicleViewModel]);

    // ── derived / memoised ───────────────────────────────────────────────

    const filteredRows = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rowsVehicles;
        return rowsVehicles.filter((v) => v.name.toLowerCase().includes(q));
    }, [rowsVehicles, search]);

    const pagedRows = useMemo(() => {
        const start = (page - 1) * PAGE_SIZE;
        return filteredRows.slice(start, start + PAGE_SIZE);
    }, [filteredRows, page]);

    const isEditing = vehicleViewModel.id != null;

    return {
        formik,
        rowsVehicles,
        loading,
        search,
        setSearch,
        page,
        setPage,
        pageSize: PAGE_SIZE,
        detailOpen,
        setDetailOpen,
        selectedVehicle,
        modalOpen,
        vehicleViewModel,
        setVehicleViewModel,
        btnSaveDisabled,
        dialogModel,
        filteredRows,
        pagedRows,
        isEditing,
        handleClickEnableVehicle,
        handleClickViewVehicle,
        handleClickEditVehicle,
        openCreate,
        closeModal,
    };
}
